import { useTranslation } from 'react-i18next';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { FaturamentoPorPeriodo, formatarValorEUR } from '@/hooks/useDashboardFinanceiro';
import { SIMBOLO_MOEDA } from '@/lib/currency';

interface GraficoFaturamentoProps {
  dados: FaturamentoPorPeriodo[];
}

export function GraficoFaturamento({ dados }: GraficoFaturamentoProps) {
  const { t } = useTranslation();
  if (!dados || dados.length === 0) {
    return (
      <div className="h-[300px] flex items-center justify-center text-muted-foreground">
        {t('common.noData')}
      </div>
    );
  }

  // Filtrar para mostrar apenas pontos relevantes se houver muitos dados
  const dadosFiltrados = dados.length > 30 
    ? dados.filter((_, index) => index % 3 === 0 || index === dados.length - 1)
    : dados;

  return (
    <div className="h-[300px]">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={dadosFiltrados} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="faturamentoGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
              <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis 
            dataKey="data" 
            tick={{ fontSize: 12 }}
            tickLine={false}
            axisLine={false}
            className="text-muted-foreground"
          />
          <YAxis 
            tick={{ fontSize: 12 }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(value) => `${SIMBOLO_MOEDA}${value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value}`}
            className="text-muted-foreground"
          />
          <Tooltip 
            content={({ active, payload, label }) => {
              if (active && payload && payload.length) {
                return (
                  <div className="bg-background border rounded-lg shadow-lg p-3">
                    <p className="font-medium text-sm">{label}</p>
                    <p className="text-sm text-primary font-bold">
                      {formatarValorEUR(payload[0].value as number)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t('dashboard.charts.osCompleted', { count: payload[0].payload.quantidade })}
                    </p>
                  </div>
                );
              }
              return null;
            }}
          />
          <Area
            type="monotone"
            dataKey="faturamento"
            stroke="hsl(var(--primary))"
            strokeWidth={2}
            fill="url(#faturamentoGradient)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
